import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

// "Zacht" buttons — rounded, soft *coloured* shadow, lift on hover, press in on
// active. Motion is smoothed by transition; the global prefers-reduced-motion
// rule in globals.css neutralises it for users who opt out.
const button = cva(
  "inline-flex items-center justify-center gap-2 font-semibold transition-[transform,box-shadow,background-color,color] duration-150 ease-out hover:-translate-y-px active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] focus-visible:outline-none",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--accent)] text-white shadow-[var(--shadow-blue)] hover:bg-[var(--accent-hover)]",
        warm:
          "bg-[var(--warm)] text-white shadow-[var(--shadow-warm)] hover:brightness-95",
        secondary:
          "bg-[var(--panel)] text-[var(--ink)] border border-[var(--border-strong)] hover:bg-[var(--panel-soft)]",
        ghost:
          "bg-transparent text-[var(--accent)] hover:bg-[var(--accent-soft)]",
      },
      size: {
        sm: "h-9 px-3 rounded-[var(--radius-sm)] text-sm",
        md: "h-11 px-5 rounded-[var(--radius)] text-sm",
        lg: "h-12 px-6 rounded-[var(--radius)] text-base",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, ...rest },
  ref,
) {
  return <button ref={ref} className={cn(button({ variant, size }), className)} {...rest} />;
});
